<template>
  <div>
    <div v-if="error" class="sk-error-msg" role="alert">{{ error }}</div>

    <h1>Connect your Amazon store</h1>
    <p class="sk-form-sub">Approve SentryKit on Amazon. It takes about a minute.</p>

    <p class="sk-region-label" id="connect-region-label">Where do you sell?</p>
    <div class="sk-region-list" role="radiogroup" aria-labelledby="connect-region-label">
      <div
        v-for="region in regions"
        :key="region.key"
        class="sk-region-row"
        :class="{ 'is-selected': region.key === regionKey }"
      >
        <label class="sk-region-radio">
          <input
            type="radio"
            name="connect-region"
            :value="region.key"
            :checked="region.key === regionKey"
            :disabled="working"
            @change="selectRegion(region.key)"
          >
          <span class="sk-region-name">{{ region.name }}</span>
          <span class="sk-region-codes">{{ region.codes.join(', ') }}</span>
        </label>
        <div v-if="region.key === regionKey" class="sk-chip-row" role="group" :aria-label="region.name + ' marketplaces'">
          <button
            v-if="region.codes.length > 1"
            type="button"
            class="sk-chip"
            :class="{ 'is-on': allOn }"
            :aria-pressed="allOn ? 'true' : 'false'"
            :disabled="working"
            @click="onToggleAll"
          >All</button>
          <button
            v-for="code in region.codes"
            :key="code"
            type="button"
            class="sk-chip"
            :class="{ 'is-on': selected.indexOf(code) !== -1, 'is-locked': region.codes.length === 1 }"
            :aria-pressed="selected.indexOf(code) !== -1 ? 'true' : 'false'"
            :disabled="working || region.codes.length === 1"
            @click="onToggleCode(code)"
          >{{ code }}</button>
        </div>
      </div>
    </div>

    <p class="sk-region-note">Selling in more than one region? Connect one now and add the others from Settings later.</p>

    <button
      type="button"
      class="sk-btn sk-btn-primary sk-connect-submit"
      :disabled="working || !canConnect"
      @click="submit"
    >{{ working ? 'Opening Amazon…' : buttonLabel }}</button>
    <p class="sk-after-btn">You'll sign in on Amazon's own page.</p>

    <p v-if="showTeammateLink" class="sk-teammate">
      Not the main Seller Central user?
      <button type="button" class="sk-text-btn" :disabled="copying" @click="$emit('copy-link')">Copy a link</button>
      for your teammate and they can connect it.
      <span v-if="linkNote" class="sk-link-note">{{ linkNote }}</span>
    </p>
  </div>
</template>

<script>
import {
  CONNECT_REGIONS,
  allCodesSelected,
  connectAuthorizationParams,
  connectButtonLabel,
  guessRegionFromBrowser,
  toggleAllCodes,
  toggleCode
} from '../utils/connectRegions'

export default {
  props: {
    working: { type: Boolean, default: false },
    copying: { type: Boolean, default: false },
    error: { type: String, default: '' },
    showTeammateLink: { type: Boolean, default: false },
    linkNote: { type: String, default: '' }
  },
  data: function () {
    var regionKey = guessRegionFromBrowser()
    var region = CONNECT_REGIONS.filter(function (r) { return r.key === regionKey })[0] || CONNECT_REGIONS[0]
    return {
      regions: CONNECT_REGIONS,
      regionKey: region.key,
      selected: region.codes.slice()
    }
  },
  computed: {
    allOn: function () {
      return allCodesSelected(this.regionKey, this.selected)
    },
    canConnect: function () {
      return !!connectAuthorizationParams(this.regionKey, this.selected)
    },
    buttonLabel: function () {
      return connectButtonLabel(this.regionKey, this.selected)
    }
  },
  methods: {
    selectRegion: function (key) {
      if (this.working) return
      var region = CONNECT_REGIONS.filter(function (r) { return r.key === key })[0]
      if (!region) return
      this.regionKey = region.key
      this.selected = region.codes.slice()
    },
    onToggleAll: function () {
      this.selected = toggleAllCodes(this.regionKey, this.selected)
    },
    onToggleCode: function (code) {
      this.selected = toggleCode(this.regionKey, this.selected, code)
    },
    submit: function () {
      var params = connectAuthorizationParams(this.regionKey, this.selected)
      if (!params || this.working) return
      this.$emit('connect', params)
    }
  }
}
</script>

<style scoped>
.sk-region-label {
  margin: 18px 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}
.sk-region-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sk-region-row {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.sk-region-row.is-selected {
  border-color: #f5841f;
  box-shadow: 0 0 0 3px rgba(245, 132, 31, 0.12);
}
.sk-region-radio {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 10px;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
}
.sk-region-radio input {
  grid-row: 1 / span 2;
  accent-color: #f5841f;
}
.sk-region-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.sk-region-codes {
  grid-column: 2;
  font-size: 12px;
  color: #64748b;
}
.sk-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 12px 12px 36px;
}
.sk-chip {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}
.sk-chip.is-on {
  background: #fff7ed;
  border-color: #f5841f;
  color: #c2410c;
}
.sk-chip:disabled {
  cursor: default;
}
.sk-region-note,
.sk-after-btn,
.sk-teammate {
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #64748b;
}
.sk-connect-submit {
  margin-top: 16px;
}
.sk-connect-submit:disabled {
  background: #cbd5e1;
  color: #475569;
  box-shadow: none;
  opacity: 1;
}
.sk-text-btn {
  background: none;
  border: 0;
  padding: 0;
  color: #f5841f;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}
.sk-link-note {
  display: block;
  margin-top: 6px;
  color: #15803d;
  font-weight: 600;
  word-break: break-all;
}
</style>



// WEBPACK FOOTER //
// ConnectRegionForm.vue?28a33aef